// src/lib/kinema/core/effects/pathInterpolator.ts
//
// Custom interpolator for pathPoints — lerps between two PathPoint[] arrays
// point-by-point, including bezier handles. Enables path morphing animations.

import type { Interpolator } from '../types';

interface Point { x: number; y: number }

interface PathPoint {
  x: number;
  y: number;
  handleIn?: Point;
  handleOut?: Point;
}

function lerpNum(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpPoint(a: Point | undefined, b: Point | undefined, t: number): Point | undefined {
  if (!a && !b) return undefined;
  const ax = a?.x ?? 0;
  const ay = a?.y ?? 0;
  const bx = b?.x ?? 0;
  const by = b?.y ?? 0;
  const x = lerpNum(ax, bx, t);
  const y = lerpNum(ay, by, t);
  // If both were undefined and result is ~0, return undefined to keep clean data
  if (!a && !b) return undefined;
  return { x, y };
}

function lerpPathPoints(from: PathPoint[], to: PathPoint[], t: number): PathPoint[] {
  // If point counts differ, interpolate up to the shorter array
  // and lerp extra points from/to themselves (they appear/disappear)
  const maxLen = Math.max(from.length, to.length);
  const result: PathPoint[] = [];

  for (let i = 0; i < maxLen; i++) {
    const a = from[i] ?? from[from.length - 1]; // clamp to last point if shorter
    const b = to[i] ?? to[to.length - 1];

    result.push({
      x: lerpNum(a.x, b.x, t),
      y: lerpNum(a.y, b.y, t),
      handleIn: lerpPoint(a.handleIn, b.handleIn, t),
      handleOut: lerpPoint(a.handleOut, b.handleOut, t),
    });
  }

  return result;
}

export const pathPointsInterpolator: Interpolator = {
  properties: ['pathPoints'],
  lerp: (from: any, to: any, t: number) => {
    if (!Array.isArray(from) || !Array.isArray(to)) {
      return t < 0.5 ? from : to;
    }
    return lerpPathPoints(from, to, t);
  },
};
